import { useState } from "react";
import { Select, SelectProps, Text, Flex } from "@mantine/core";
import cx from "clsx";
import styles from "@scss/custom-components/dataset-select.module.scss";

export type variant = "live" | "completed" | "upcoming" | "replaying";

export interface EventOption {
  id: string;
  name: string;
  category?: string;
  status: variant;
}

interface CwiggoDatasetSelectProps extends Omit<SelectProps, "data" | "onChange"> {
  data: EventOption[];
  onCwiggoChange?: (value: string | null, option?: EventOption) => void;
}

const SelectItem = ({ item }: { item: EventOption }) => {
  const { name, category, status } = item;
  const fullLabel = category ? `${name} - ${category}` : name;

  return (
    <Flex className={styles.itemWrapper}>
      <Flex align="center" className={styles.itemText}>
        <Flex align="center" className={styles.labelTruncate} title={fullLabel}>
          <Text unstyled size="sm" className={styles.itemTitle} truncate="end">
            {name}
          </Text>
          {category && (
            <Text unstyled size="xs" className={styles.itemCategory}>
              &nbsp;- {category}
            </Text>
          )}
        </Flex>

        <Text
          unstyled
          size="xs"
          className={cx(styles.statusLabel, styles[`statusLabel--${status}`])}
        >
          [{status}]
        </Text>
      </Flex>
    </Flex>
  );
};

export const CustomDatasetSelect = ({
  data,
  onCwiggoChange,
  className,
  classNames: classNamesProp = {},
  error,
  style,
  ...props
}: CwiggoDatasetSelectProps & { style?: React.CSSProperties }) => {
  const [selected, setSelected] = useState<string | null>(null);

  const selectedOption = data.find((o) => o.name === selected);
  const selectedVariant: variant | null = selectedOption?.status ?? null;

  // Apply variant modifier to the wrapper, not the input
  const wrapperClass = cx(
    styles.selectWrapperOuter,
    selectedVariant && styles[`select--${selectedVariant}`],
    className,
  );

  const apiClasses = {
    root: styles.root,
    wrapper: styles.wrapper,
    input: styles.input,
    dropdown: styles.dropdown,
    options: styles.options,
    option: styles.option,
    section: styles.rightSection,
    label: styles.label,
    error: styles.error,
  };

  const handleChange = (val: string | null) => {
    setSelected(val);
    const matched = data.find((o) => o.name === val);
    onCwiggoChange?.(val, matched);
  };

  const wrapperStyle: React.CSSProperties = {
    minWidth: 0,
    ...style,
  };

  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <Select
        {...props}
        data={data.map((ev) => ({
          value: ev.id,
          label: ev.category ? `${ev.name} - ${ev.category}` : ev.name,
          data: ev,
        }))}
        renderOption={({ option }) => {
          const fullItem = data.find((d) => d.id === option.value);
          return fullItem ? <SelectItem item={fullItem} /> : null;
        }}
        value={selected}
        variant="darker"
        onChange={handleChange}
        error={error}
        classNames={{ ...apiClasses, ...classNamesProp }}
        rightSection={
          <></>
        }
        placeholder="Select dataset"
      />

      {selectedVariant && (
        <Text
          unstyled
          size="xs"
          className={cx(
            styles.selectStatusLabel,
            styles[`statusLabel--${selectedVariant}`],
          )}
        >
          [{selectedVariant}]
        </Text>
      )}
    </div>
  );
};
